import { FC, useEffect, useMemo, useState, useCallback, ChangeEvent } from 'react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import utc from 'dayjs/plugin/utc';
import Link from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/router';
import memoize from 'memoize-one';
import { useDispatch } from 'react-redux';
import { useImageLoaded } from 'src/hooks';

dayjs.extend(utc);

import { Article, WithContext } from 'schema-dts';
import Breadcrumbs, { Crumb } from 'modules/common/components/Breadcrumbs';
import InternalBlogSNSShareRow from './InternalBlogSNSShareRow';
import InternalBlogAuthorSection from './InternalBlogAuthor';
import JournalGroup from 'components/molecules/JournalGroup';
import ProCTALink from 'components/atoms/ProCTALink';
import Button from '@z-components/atoms/Button';
import InputBox from '@z-components/atoms/InputBox';
import { getJobTypeDisplayName } from 'utils/job-type';

import type { InternalBlogPost, BlogPost } from 'modules/marketing-pages/marketing-page.types';
import type { VerticalPageJobType } from 'modules/job-types/types';
import { openNewRequestModal } from 'modules/requests/service';
import { addressFormSSKey, setItem as setSSItem } from 'utils/sessionStorage';

import styles from './InternalBlogPage.module.scss';
import wordPressBlockStyles from './WordPressBlockStyleVer_5_8_1.module.scss';
import {
  CTATypes,
  getUtmDataByQueryParamForInternalBlog,
  modifyUtmParamsWithCTAType,
} from 'utils/internal-blog';
import { isUserAuthenticated, logEvent } from 'utils/analytics';
import { setItem } from 'utils/webStorage';

import IconCreateDate from 'images/icons/Icon_Created_Date.svg';
import IconUpdatedDate from 'images/icons/Icon_Updated_Date.svg';
import InternalBlogStickyCTASection from './InternalBlogStickyCTASection';
import { LinkGroup } from 'modules/common/components/InternalLinkGroups';
import { listPrefectures } from 'modules/locations/service';
import { InternalBlogLINERow } from './InternalBlogLINERow';

const WP_IMG_ERROR_HANDLER = `<img onerror="this.style.display='none';" `;
const DATESTRING_DISPLAY_FORMAT = 'YYYY/MM/DD';
const DATETIMESTRING_ARTICLE_STRUCTURE_FORMAT = 'YYYY-MM-DDThh:mm:ssZ';
const JST_OFFSET = 9;
const getJSTFromUTCDateString = (epochTime: number, format?: string) =>
  dayjs(epochTime)
    .utcOffset(JST_OFFSET)
    .format(format ? format : DATESTRING_DISPLAY_FORMAT);

export const getBlogStructuredData = memoize((blogPost: InternalBlogPost): string => {
  const formattedDatePublished = getJSTFromUTCDateString(
    blogPost.createdTime,
    DATETIMESTRING_ARTICLE_STRUCTURE_FORMAT
  );
  const formattedDateModified = getJSTFromUTCDateString(
    blogPost.updatedTime,
    DATETIMESTRING_ARTICLE_STRUCTURE_FORMAT
  );

  const structuredData: WithContext<Article> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blogPost.title,
    image: [blogPost.featuredImageUrl ?? ''],
    datePublished: formattedDatePublished,
    author: [
      {
        '@type': 'Person',
        name: blogPost.authorName,
        description: blogPost.authorDescription,
      },
    ],
    publisher: [
      {
        '@type': 'Organization',
        name: 'ゼヒトモ',
        url: 'https://zehitomo.com',
      },
    ],
  };

  if (formattedDateModified !== formattedDatePublished) {
    structuredData.dateModified = formattedDateModified;
  }

  return JSON.stringify(structuredData);
});

interface Props {
  blogPost: InternalBlogPost;
  crumbs: Array<Crumb & { isVertical?: boolean }>;
  jobType: VerticalPageJobType;
  relatedPosts: Array<BlogPost>;
}

// GROW-112 Layout template to differentiate blog appearance depending on settigns from wordpress
const BLOG_LAYOUT_TEMPLATE_1 = 'template_1';
const BLOG_LAYOUT_TEMPLATE_2 = 'template_2';
const BLOG_DEFAULT_LAYOUT_TEMPLATE = BLOG_LAYOUT_TEMPLATE_1;

const InternalBlogPage: FC<Props> = ({
  blogPost: {
    authorDescription,
    authorImageUrl,
    authorName,
    createdTime,
    featuredImageUrl,
    htmlContent,
    layoutTemplate,
    title,
    updatedTime,
  },
  crumbs,
  jobType,
  relatedPosts,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const formattedCreatedDate = getJSTFromUTCDateString(createdTime);
  const formattedUpdatedDate = getJSTFromUTCDateString(updatedTime);
  const [imgSrc, setImgSrc] = useState(featuredImageUrl);
  const [address, setAddress] = useState('');
  const [prefectureLinkGroup, setPrefectureLinkGroup] = useState<LinkGroup | null>(null);
  const router = useRouter();
  const dispatch = useDispatch();

  // HACK: prefix every opening image tag with an onerror to hide broken images. Why? This is the same behavior
  // that exists in WP.
  const htmlContentWithImageOnErrorHandler = useMemo(
    () => htmlContent.replaceAll('<img ', WP_IMG_ERROR_HANDLER),
    [htmlContent]
  );
  const handleReportImageError = useCallback(() => {
    setImgSrc('');
  }, []);
  const handleImageLoad = useCallback(
    ({ width, height }) => {
      if (width === 0 || height === 0) {
        handleReportImageError();
      }
    },
    [handleReportImageError]
  );
  const [ref] = useImageLoaded(handleImageLoad);

  const sectionCTATitleTextTranslationId = 'internalBlog:ctaSection:title';
  const sectionCTAButtonTextTranslationId = 'internalBlog:ctaSection:searchPro';

  const { query } = router;
  const utmForInternalBlog = getUtmDataByQueryParamForInternalBlog(query);
  const handleMakeRequestButton = () => {
    if (utmForInternalBlog) {
      setItem(
        'ngStorage-utm',
        JSON.stringify(modifyUtmParamsWithCTAType(utmForInternalBlog, CTATypes.fixed))
      );
      logEvent(
        `NewRequest:${jobType.id}`,
        'startNewRequest:fixedCTAClicked',
        isUserAuthenticated() ? 'authenticated' : 'unauthenticated'
      );
    }

    openNewRequestModal({
      context: { address },
      dispatch,
      properties: { initialValues: { address }, jobTypeId: jobType.id },
    });
    setSSItem(addressFormSSKey, address);
  };

  useEffect(() => {
    // @ts-expect-error add this to supres error for window element
    window.openRequestFormFromArticleCTAButton = (data: {
      buttonLabel: string;
      campaignName: string;
    }) => {
      if (data.buttonLabel && data.campaignName) {
        if (utmForInternalBlog) {
          setItem(
            'ngStorage-utm',
            JSON.stringify(modifyUtmParamsWithCTAType(utmForInternalBlog, data.campaignName))
          );
        }
        logEvent(
          `NewRequest:${jobType.id}`,
          `startNewRequest:${data.campaignName.trim()}CTAClicked`,
          data.buttonLabel
        );
      }

      openNewRequestModal({
        context: { address },
        dispatch,
        properties: { initialValues: { address }, jobTypeId: jobType.id },
      });
    };
    return () => {
      // @ts-expect-error add this to supres error for window element
      delete window.openRequestFormFromArticleCTAButton;
    };
  }, [address, dispatch, jobType.id, utmForInternalBlog]);

  useEffect(() => {
    listPrefectures({
      jobTypeId: jobType.id,
      prefectureKey: '',
    })
      .then((prefectures) => {
        const prefix = language === 'en' ? '/en' : '';
        const linkGroup = {
          links: prefectures.map(({ key, name }) => ({
            route: `${prefix}/${jobType.categories.join('/')}/${jobType.id}/${key ?? ''}`,
            text: name,
          })),
          title: t('internalBlog:availablePrefectures'),
        };
        setPrefectureLinkGroup(linkGroup);
      })
      .catch(() => {
        setPrefectureLinkGroup(null);
      });
  }, [jobType.id]);

  let selectedLayoutTemplate = layoutTemplate;
  if (![BLOG_LAYOUT_TEMPLATE_1, BLOG_LAYOUT_TEMPLATE_2].includes(selectedLayoutTemplate)) {
    selectedLayoutTemplate = BLOG_DEFAULT_LAYOUT_TEMPLATE;
  }

  return (
    <div className={styles.root}>
      <div className={styles['crumb-container']}>
        <Breadcrumbs align="center" crumbs={crumbs} />
      </div>
      <div className={styles['title-container']}>
        <h1 className={styles['title']}>{title}</h1>
        <div className={styles['date']}>
          <IconCreateDate height={12} width={12} /> <span>{formattedCreatedDate}</span>
          {formattedCreatedDate !== formattedUpdatedDate && (
            <>
              <IconUpdatedDate height={12} width={12} /> <span>{formattedUpdatedDate}</span>
            </>
          )}
        </div>
      </div>
      <div className={styles['banner-container']}>
        {imgSrc && (
          <>
            <img
              className={styles['banner-image']}
              ref={ref}
              src={imgSrc}
              alt={title}
              onError={handleReportImageError}
            />
            <Head>
              <link rel="preload" as="image" href={imgSrc} />
            </Head>
          </>
        )}
        <div className={styles['form-container']}>
          <h3 className={styles['cta-text-container']}>
            {t('internalBlog:ctaSection.title', { jobType: jobType.translations.requestType })}
          </h3>
          <InputBox
            centerLabel={true}
            isLong={true}
            name="address"
            // eslint-disable-next-line react/jsx-no-bind
            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setAddress(event.target.value)
            }
            placeholder={t('marketingPages:requestFormEntryForm.placeholder')}
          />
          <Button
            className={classnames(
              styles['animate-button'],
              styles['drop-shadow-button'],
              'z-btn z-btn-full z-btn-action'
            )}
            dataGtmClick={'gtm-requestButton-internalBlog-blogFirstCTA'}
            id={'fixed-CTA'}
            onClick={handleMakeRequestButton}
            testId="start-new-request"
            text={t('internalBlog:ctaSection.searchPro')}
          />
        </div>
      </div>
      <div className={classnames(styles.grid, styles['grid-padding'])}>
        <div className={styles['sns-share-row']}>
          <InternalBlogSNSShareRow iconSize="medium" title={title} />
        </div>
        <div
          className={classnames(
            styles['wordpress-container'],
            styles.grid,
            wordPressBlockStyles['wp-block-styles']
          )}
          dangerouslySetInnerHTML={{ __html: htmlContentWithImageOnErrorHandler }}
        />
        {prefectureLinkGroup && (
          <section>
            <h2>{prefectureLinkGroup.title}</h2>
            <div className={styles['available-prefectures-container']}>
              {prefectureLinkGroup.links.map(({ route, text }) => (
                <Link key={route} prefetch={false} href={route}>
                  <a className={styles['pill']}>{text}</a>
                </Link>
              ))}
            </div>
          </section>
        )}
        <section>
          <h2>{t('internalBlog:addLINE')}</h2>
          <div className={styles['sns-share-row']}>
            <InternalBlogLINERow />
          </div>
        </section>

        <section>
          <ProCTALink
            eventType={'blogProCTAClicked'}
            dataGtmClick="gtm-proCTA-internalBlog-blog_vertical"
          />
        </section>

        {selectedLayoutTemplate === BLOG_LAYOUT_TEMPLATE_1 && (
          <section>
            <h2>{t('internalBlog:shareSNS')}</h2>
            <div className={styles['sns-share-row']}>
              <InternalBlogSNSShareRow iconSize="small" title={title} />
            </div>
          </section>
        )}
        <section>
          <h2>{t('internalBlog:relatedTags')}</h2>
          <div className={styles['tag-row']}>
            {crumbs
              .filter((crumb) => crumb.text !== title)
              .map(({ link, text }) => (
                <Link key={link} href={link}>
                  <a className={styles.tag}>{text}</a>
                </Link>
              ))}
          </div>
        </section>
        {authorImageUrl && authorName && (
          <section>
            <h2>{t('internalBlog:blogAuthor')}</h2>
            <InternalBlogAuthorSection
              authorDescription={authorDescription}
              authorImageUrl={authorImageUrl}
              authorName={authorName}
            />
          </section>
        )}
        {selectedLayoutTemplate === BLOG_LAYOUT_TEMPLATE_2 && (
          <section>
            <h2>{t('internalBlog:shareSNS')}</h2>
            <div className={styles['sns-share-row']}>
              <InternalBlogSNSShareRow iconSize="small" title={title} />
            </div>
          </section>
        )}
      </div>
      <section className={styles['journal-section']}>
        <h2>{`${getJobTypeDisplayName(jobType)}${t('internalBlog:relatedBlogSectionTitle')}`}</h2>
        <JournalGroup posts={relatedPosts}></JournalGroup>
      </section>
      <section>
        <InternalBlogStickyCTASection
          dataGtmClick="gtm-requestButton-internalBlog-blogSticky"
          internalBlogUtmParams={utmForInternalBlog}
          jobType={jobType}
          sectionButtonTranslationId={sectionCTAButtonTextTranslationId}
          sectionTitleTranslationId={sectionCTATitleTextTranslationId}
        />
      </section>
    </div>
  );
};

export type { Props as InternalBlogPageProps };
export default InternalBlogPage;
